import React, { useState, useEffect } from 'react';
import { Camera, User } from 'lucide-react';
import { parse, addMinutes, format, isBefore } from 'date-fns';
import "./timetable.css";
import api from '../../../service/caller';

const isTimeInSchedule = (timeSlot, startTime, endTime) => {
  const [slotStart, slotEnd] = timeSlot.split(' - ');
  const scheduleStart = parse(startTime, 'HH:mm', new Date());
  const scheduleEnd = parse(endTime, 'HH:mm', new Date());
  const slotStartTime = parse(slotStart, 'HH:mm', new Date());
  const slotEndTime = parse(slotEnd, 'HH:mm', new Date());

  return slotStartTime >= scheduleStart && slotEndTime <= scheduleEnd;
};

const TimeSlot = ({ selection, onSelect, time, isAvailable = true, isPast = false, isReserved = false, appointmentType = '' }) => {
  const [showIcons, setShowIcons] = useState(false);

  const handleClick = () => {
    if (!selection && isAvailable && !isPast && !isReserved) {
      setShowIcons(!showIcons);
    }
  };

  const getBackgroundColor = () => {
    if (isReserved) {
      return 'bg-red-100 cursor-not-allowed';  // Couleur rouge clair pour les créneaux réservés
    }
    if (isPast) {
      return 'bg-blue-200 cursor-not-allowed';  // Couleur bleue claire et non cliquable
    }
    if (selection) {
      return 'bg-pink-100';
    }
    if (!isAvailable) {
      return 'bg-gray-300 opacity-50 cursor-not-allowed';
    }
    return 'bg-green-100';
  };

  const getIcon = () => {
    if (isReserved) {
      return appointmentType === 'camera' ? (
        <Camera className="w-4 h-4" />
      ) : (
        <User className="w-4 h-4" />
      );
    }

    if (selection === 'camera') {
      return <Camera className="w-4 h-4" />;
    }
    if (selection === 'person') {
      return <User className="w-4 h-4" />;
    }
    return null;
  };

  return (
    <div
      className={`relative p-2 border border-gray-200 h-12 cursor-pointer
        ${getBackgroundColor()}
        hover:opacity-80
        flex items-center justify-center timetablelast
      `}
      onClick={handleClick}
    >
      {getIcon()}

      {showIcons && !selection && isAvailable && !isPast && !isReserved && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center gap-4 z-10">
          <Camera
            className="w-6 h-6 hover:text-blue-500 cursor-pointer"
            onClick={() => {
              onSelect('camera', time);
              setShowIcons(false);
            }}
          />
          <User
            className="w-6 h-6 hover:text-blue-500 cursor-pointer"
            onClick={() => {
              onSelect('person', time);
              setShowIcons(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

const CustomScheduler = ({ doctorSchedule, onAppointmentSaved, doctorId }) => {
  const [selections, setSelections] = useState({});
  const [currentWeek, setCurrentWeek] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [days, setDays] = useState([]);
  const [duration_unit, setDurationunit] = useState(30); // Default duration
  const [modalData, setModalData] = useState(null);
  const [savedAppointment, setSavedAppointment] = useState(null);
  const [savedAppointments, setSavedAppointments] = useState([]);
  const [weeks, setWeeks] = useState([]); // Liste des semaines disponibles
  const [selectedScheduleIndex, setSelectedScheduleIndex] = useState(0); // Index pour le calendrier sélectionné

  // Récupérer les créneaux réservés via l'API avec axios
  useEffect(() => {
    const fetchReservedSlots = async () => {
      try {
        const response = await api.get(`/clinic/reservedSlots?doctorId=${doctorId}`);
        setSavedAppointments(response.data.reservedSlots || []); // Stocke les créneaux réservés
      } catch (error) {
        console.error('Error fetching reserved slots:', error);
      }
    };

    if (doctorId) {
      fetchReservedSlots();
    }
  }, [doctorId]);

  // Vérification si le créneau est réservé en tenant compte uniquement des heures
  const isSlotReserved = (day, timeSlot) => {
    // Extraire l'heure de début du créneau
    const slotStartTime = timeSlot.split(' - ')[0];
    
    return savedAppointments.some(appointment => {
      // Vérifier si l'appointment est le même jour
      const date = new Date(appointment.appointmentDate);
      const dayOfWeek = date.getDay();
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      if (!days[dayOfWeek].includes(day)) {
        return false;
      }
      
      // Comparer uniquement les heures de début
      return appointment.appointmentTime === slotStartTime;
    });
  };

  useEffect(() => {
    if (doctorSchedule && doctorSchedule.length > 0) {
      updateScheduleDisplay(selectedScheduleIndex);
    }
  }, [doctorSchedule, selectedScheduleIndex]);

  const updateScheduleDisplay = (index) => {
    if (!doctorSchedule || index >= doctorSchedule.length) return;

    const scheduleItem = doctorSchedule[index];

    const startDate = new Date(scheduleItem.start_date);
    const endDate = new Date(scheduleItem.end_date);
    const availableWeeks = [];

    // Calculer la plage de semaines disponibles
    let currentStartDate = startDate;
    while (currentStartDate <= endDate) {
      const currentEndDate = addMinutes(currentStartDate, 7 * 24 * 60); // 7 jours
      availableWeeks.push({ startDate: currentStartDate, endDate: currentEndDate });
      currentStartDate = addMinutes(currentStartDate, 7 * 24 * 60); // Avancer à la semaine suivante
    }

    setWeeks(availableWeeks);
    if (availableWeeks.length > 0) {
      setCurrentWeek(availableWeeks[0]);  // Par défaut, afficher la première semaine
    }

    const generateTimeSlots = () => {
      const slots = [];
      const workDays = scheduleItem.schedule
        .filter(day => day.start_time && day.end_time)
        .map(day => day.day);

      setDays(workDays);

      // Set duration from the first available schedule
      const firstSchedule = scheduleItem.schedule.find(day => day.start_time && day.end_time);
      if (firstSchedule && firstSchedule.duration_unit) {
        setDurationunit(firstSchedule.duration_unit);
      }

      scheduleItem.schedule.forEach(daySchedule => {
        if (daySchedule.start_time && daySchedule.end_time) {
          const startTime = parse(daySchedule.start_time, 'HH:mm', new Date());
          const endTime = parse(daySchedule.end_time, 'HH:mm', new Date());
          const duration = daySchedule.duration_unit || 30;

          let currentSlot = startTime;
          while (currentSlot < endTime) {
            const slotEnd = addMinutes(currentSlot, duration);

            if (slotEnd <= endTime) {
              slots.push(
                `${format(currentSlot, 'HH:mm')} - ${format(slotEnd, 'HH:mm')}`
              );
            }

            currentSlot = slotEnd;
          }
        }
      });

      setTimeSlots([...new Set(slots)]);
    };

    generateTimeSlots();
  };

  const handleSelection = (type, time, day) => {
    const slotKey = `${day}-${time}`;
    setSelections((prev) => ({
      ...prev,
      [slotKey]: type,
    }));
    setModalData({ type, time, day });
  };

  const handleCloseModal = () => {
    if (modalData) {  // Vérifie si modalData est défini
      setSelections((prev) => {
        const newSelections = { ...prev };
        const slotKey = `${modalData.day}-${modalData.time}`;
        delete newSelections[slotKey]; // Supprime la sélection du rendez-vous en cours
        return newSelections;
      });
    }
    setModalData(null);  // Réinitialiser le modalData
  };

  const changeWeek = (direction) => {
    setCurrentWeek((prev) => {
      const currentIndex = weeks.findIndex(
        (week) => week.startDate.getTime() === prev.startDate.getTime()
      );

      let newIndex = currentIndex + (direction === "prev" ? -1 : 1);
      if (newIndex < 0) newIndex = 0;
      if (newIndex >= weeks.length) newIndex = weeks.length - 1;

      return weeks[newIndex];
    });
  };

  const changeSchedule = (direction) => {
    let newIndex = selectedScheduleIndex + (direction === "prev" ? -1 : 1);
    if (newIndex < 0) newIndex = 0;
    if (newIndex >= doctorSchedule.length) newIndex = doctorSchedule.length - 1;
    
    setSelectedScheduleIndex(newIndex);
  };

  const formatDateRange = () => {
    if (!currentWeek || !currentWeek.startDate || !currentWeek.endDate) return "";
    
    const { startDate, endDate } = currentWeek;
    return `${startDate.getDate()} ${startDate.toLocaleString("default", { month: "long" })} ${startDate.getFullYear()} - ${endDate.getDate()} ${endDate.toLocaleString("default", { month: "long" })}`;
  };

  const formatScheduleTitle = () => {
    if (!doctorSchedule || doctorSchedule.length === 0) return "No Schedule";
    
    const current = doctorSchedule[selectedScheduleIndex];
    // You can customize this to show whatever information you have in your schedule item
    return `Schedule ${selectedScheduleIndex + 1} of ${doctorSchedule.length}`;
  };

  const handleSaveAppointment = () => {
    if (!modalData) return;

    const { startDate } = currentWeek;

    // Créer une nouvelle instance de Date pour la date complète
    const appointmentDate = new Date(startDate);

    // Ajuster la date pour le jour sélectionné
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayIndex = daysOfWeek.indexOf(modalData.day); // Trouver l'index du jour sélectionné
    appointmentDate.setDate(startDate.getDate() + dayIndex); // Déplacer la date de la semaine au jour sélectionné

    // Extraire l'heure de début du créneau sélectionné
    const timeStart = modalData.time.split(' - ')[0];
    const appointmentTime = timeStart;  // Utiliser directement l'heure de début sans reformatage

    // Vérification si le rendez-vous est dans le futur
    const [hours, minutes] = timeStart.split(':').map(num => parseInt(num, 10));
    const appointmentDateTime = new Date(appointmentDate);
    appointmentDateTime.setHours(hours, minutes, 0, 0);
    
    if (appointmentDateTime <= new Date()) {
      alert("La date sélectionnée est déjà passée. Veuillez choisir une autre date.");
      return;
    }

    // Crée un objet avec toutes les données sélectionnées, séparées pour la date et l'heure
    const appointmentschedule = {
      date: appointmentDate,     // Date complète sans l'heure
      time: appointmentTime,     // Heure séparée
      type: modalData.type,      // 'camera' ou 'person'
      durationApt: duration_unit,
      scheduleId: doctorSchedule[selectedScheduleIndex].id || null, // Include schedule ID if available
    };

    // Appel à une fonction passée en props pour envoyer les données au parent (ou sauvegarder)
    if (onAppointmentSaved) {
      onAppointmentSaved(appointmentschedule);
    }

    // Sauvegarder l'état du rendez-vous pour affichage (confirmation)
    setSavedAppointment(appointmentschedule);
    setModalData(null); // Réinitialiser les données du modal
  };

  // If there's no doctorSchedule or it's empty, show a message
  if (!doctorSchedule || doctorSchedule.length === 0) {
    return <div className="p-4 text-center">No schedule available</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 timetablelast">
      {/* Schedule selector if there are multiple schedules */}
      {doctorSchedule.length > 1 && (
        <div className="flex justify-between items-center mb-4">
          <button 
            className="p-2 bg-gray-200 rounded" 
            onClick={() => changeSchedule("prev")}
            disabled={selectedScheduleIndex === 0}
          >
            &lt;
          </button>
          <h2 className="text-lg font-semibold">{formatDateRange()}</h2>
          <button 
            className="p-2 bg-gray-200 rounded" 
            onClick={() => changeSchedule("next")}
            disabled={selectedScheduleIndex === doctorSchedule.length - 1}
          > &gt;
          </button>
        </div>
      )}

      Week selector
      {/* <div className="flex justify-between items-center mb-4">
        <button 
          className="p-2" 
          onClick={() => changeWeek("prev")}
          disabled={weeks.length === 0 || weeks.indexOf(currentWeek) === 0}
        >
          &lt;
        </button>
        <h2 className="text-lg font-semibold">{formatDateRange()}</h2>
        <button 
          className="p-2" 
          onClick={() => changeWeek("next")}
          disabled={weeks.length === 0 || weeks.indexOf(currentWeek) === weeks.length - 1}
        >
          &gt;
        </button>
      </div> */}

      <div className="grid grid-cols-[auto_repeat(7,1fr)] gap-1">
        <div className="font-semibold p-2">Time</div>
        {days.map((day) => (
          <div key={day} className="font-semibold p-2 text-center">{day}</div>
        ))}

        {timeSlots.map((time) => (
          <React.Fragment key={time}>
            <div className="p-2 text-sm">{time}</div>
            {days.map((day) => {
              const isReserved = isSlotReserved(day, time);
              const appointment = savedAppointments.find(
                (apt) => apt.appointmentTime === time.split(' - ')[0] && apt.appointmentDate.includes(day)
              );
              const appointmentType = appointment ? appointment.type_consultation : '';
              
              // Check if this timeslot is available in the current schedule
              const scheduleItem = doctorSchedule[selectedScheduleIndex];
              const isAvailable = scheduleItem.schedule.some(
                schedule =>
                  schedule.day === day &&
                  isTimeInSchedule(time, schedule.start_time, schedule.end_time)
              );

              // Calculer si le créneau est passé
              const currentDate = new Date();
              const timeSlotDate = new Date(currentWeek.startDate);
              const [hour, minute] = time.split(' - ')[0].split(':').map((t) => parseInt(t, 10));
              const dayIndex = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].indexOf(day);
              timeSlotDate.setDate(timeSlotDate.getDate() + dayIndex);  // Modifier la date en fonction du jour de la semaine
              timeSlotDate.setHours(hour, minute);  // Définir l'heure du créneau

              const isPast = timeSlotDate < currentDate;  // Si la date est dans le passé

              return (
                <TimeSlot
                  key={`${day}-${time}`}
                  time={time}
                  isAvailable={isAvailable}
                  selection={selections[`${day}-${time}`]}
                  isPast={isPast}  // Passer l'indicateur de "passé"
                  onSelect={(type) => handleSelection(type, time, day)}
                  isReserved={isReserved}
                  appointmentType={appointmentType}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <div className="modal-overlay">
          <div className="modal-content1">
            <p className="modal-text">
              Create appointment for <strong>{modalData.day}</strong>, <strong>{modalData.time}</strong>?
            </p>
            <div className="modal-buttons">
              <button
                className="modal-button close"
                onClick={handleCloseModal}
              >
                Close
              </button>
              <button
                className="modal-button confirm"
                onClick={handleSaveAppointment}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomScheduler;